"use client"

import { ComponentConfig } from "@measured/puck"
import { Form, Spin } from "antd"
import { useState } from "react"
import "swiper/css"
import "swiper/css/pagination"
import { twMerge } from "tailwind-merge"
import { configs } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface ContactItem {
  icon?: string
  itemTitle?: string
  itemContent?: string
}

export interface ContactProps extends CommonStylesProps {
  type: "fluid" | "custom" | "boxed"
  gapCustom?: string
  heightContact?: string
  title?: string
  subTitle?: string
  contactItem: ContactItem[]
}

export const RenderConfig: ComponentConfig<ContactProps> = {
  render: ({ className, type, styles, gapCustom, responsiveType, heightContact, title, subTitle, contactItem }) => {
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState("")
    const [isPopupVisible, setIsPopupVisible] = useState(false)

    const [formData, setFormData] = useState({
      emailTo: "",
      yourName: "",
      email: "",
      phone: "",
      message: "",
    })

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      try {
        await form.validateFields()
      } catch (error) {
        return
      }

      setIsLoading(true)
      try {
        const response = await fetch("/api/email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: "[Thông báo] Liên hệ với chúng tôi",
            emailTo: formData.emailTo,
            content: `Tên của bạn: ${formData.yourName},\nEmail: ${formData.email},\nSố điện thoại: ${formData.phone}\nTin nhắn: ${formData.message}`,
          }),
        })

        if (!response.ok) {
          throw new Error("Failed to submit data")
        }
        setIsPopupVisible(true)
        setFormData({
          emailTo: "",
          yourName: "",
          email: "",
          phone: "",
          message: "",
        })
        form.resetFields()
        setMessage("Đăng ký tư vấn thành công!")
      } catch (error) {
        setMessage("Đã xảy ra lỗi khi đăng ký!")
      } finally {
        setIsLoading(false)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target
      setFormData(prev => ({ ...prev, [name]: value }))
    }
    const closePopup = () => {
      setIsPopupVisible(false)
      form.resetFields()
    }

    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div
          id={id}
          className={twMerge(
            className,
            `w-full flex lg:flex-row flex-col ${
              Number(title?.trim().length) > 1 ? "items-end" : "items-start"
            } justify-between gap-10 lg:h-[388px] h-full`,
          )}
        >
          <div className="max-w-[463px]">
            <h2
              className="font-bold lg:text-5xl text-[40px] lg:text-left text-center text-[#242F3E]"
              style={{ lineHeight: "64px" }}
            >
              {title}
            </h2>
            {subTitle && <p>{subTitle}</p>}
            <div
              className={`flex flex-col lg:gap-10 gap-4  ${
                Number(title?.trim().length) > 1 && "lg:mt-16 mt-[40px]"
              }  lg:max-w-[418px] max-w-[311px]`}
            >
              {contactItem.map((item, index) => {
                return (
                  <div key={index} className="flex flex-row items-start gap-2">
                    {item.icon && (
                      <img src={`${configs.API_URL}${item.icon}`} alt={item.itemTitle} className="h-6 w-6" />
                    )}
                    <div className="flex flex-col gap-1">
                      {item.itemTitle && <p className="font-bold text-base text-[#242F3E]">{item.itemTitle}</p>}
                      {item.itemContent && <p className="text-[#4F4F4F] text-base font-normal">{item.itemContent}</p>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <form className="flex flex-col gap-4 w-full items-center max-w-[463px]" onSubmit={handleSubmit}>
            <input
              name="yourName"
              type="text"
              className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full placeholder-[#8E8E93]"
              placeholder="Your name"
              onChange={handleInputChange}
              required
            />
            <input
              name="email"
              type="email"
              className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full placeholder-[#8E8E93]"
              placeholder="Email"
              onChange={handleInputChange}
              required
            />
            <input
              name="phone"
              type="tel"
              className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full placeholder-[#8E8E93]"
              placeholder="Phone"
              pattern="^[0-9]{10,11}$"
              onChange={handleInputChange}
              required
            />
            <textarea
              name="message"
              className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full resize-none placeholder-[#8E8E93]"
              placeholder="Message"
              onChange={handleInputChange}
              style={{ height: "6rem" }}
            ></textarea>
            <button
              type="submit"
              className="bg-[#0B9444] rounded-xl w-full flex flex-row items-center justify-center gap-2 py-3"
            >
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.7916 8.9502L8.85059 11.8912"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.4419 4.68417L13.5049 16.4942C13.2039 17.3972 11.9729 17.5112 11.5109 16.6792L8.85095 11.8912L4.06294 9.23117C3.23094 8.76917 3.34494 7.53817 4.24794 7.23717L16.0579 3.30017C16.9129 3.01517 17.7269 3.82917 17.4419 4.68417Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-white font-semibold text-base" style={{ lineHeight: "22px" }}>
                Send Us
              </p>
            </button>
            {isLoading && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <Spin />
              </div>
            )}

            {isPopupVisible && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-8 rounded shadow-md relative">
                  <button
                    onClick={closePopup}
                    className="absolute top-0 text-[30px] right-2 text-gray-600 hover:text-gray-800"
                    aria-label="Close"
                  >
                    &times;
                  </button>
                  <p className="text-lg font-semibold">{message}</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </>
    )
  },
}
